/**
 * Конверт (A6): деньги спрятаны за тапом. Свёрнут — одна строка
 * «Конверт»; раскрыт — сбережения, долг, доход (через `money`) и действия.
 * «Погасить долг»: платит min(сбережения, долг) в конце года через стор
 * (`payDebt` ставится в очередь на следующий тик).
 *
 * Мини-игры раздела E (план 2b, Task 9): кнопки «Лотерея», «Казино: …»,
 * «Кости», «Вложить в …», «Занять» — ровно по списку `minigames`, который
 * стор уже отфильтровал по возрасту, стране, деньгам и настройкам
 * (`game.minigamesAvailable({ kind: 'envelope' })`). Когда в этом году
 * исход уже сыгран (`busy`), кнопки выключены с причиной в тексте.
 * Остальные действия (отложить, перевести) — текстом «появится позже».
 */
import type { Life } from '@life/engine';
import { t } from '@life/i18n';
import type { MinigameDef } from '@life/minigames';
import { useState } from 'preact/hooks';
import { money } from '../lib/format';

export interface EnvelopeProps {
  life: Life;
  onPayDebt: (amount: number) => void;
  /** Доступные мини-игры конверта (раздел E); по умолчанию ничего. */
  minigames?: readonly MinigameDef[];
  onOpen?: (id: string) => void;
  /** Мини-игра уже открыта или её исход ждёт конца года: кнопки выключены. */
  busy?: boolean;
}

const LATER_ACTIONS = ['save', 'send'] as const;

export function Envelope({ life, onPayDebt, minigames = [], onOpen, busy = false }: EnvelopeProps) {
  /** Сумма, поставленная в очередь на этот год, и возраст, в котором нажали. */
  const [queued, setQueued] = useState<{ age: number; sum: number } | null>(null);
  const pending = queued !== null && queued.age === life.age ? queued.sum : 0;
  const inDebt = life.debt > 0;
  const canPay = inDebt && life.money > 0 && pending === 0;
  const sum = Math.min(life.money, life.debt);

  const pay = () => {
    if (!canPay) return;
    setQueued({ age: life.age, sum });
    onPayDebt(sum);
  };

  return (
    <details class="envelope">
      <summary>{t('ui.envelope')}</summary>
      <dl class="kv">
        <dt>{t('ui.envelope.savings')}</dt>
        <dd class="mono">{money(life.money)}</dd>
        <dt>{t('ui.envelope.debt')}</dt>
        <dd class="mono">{money(life.debt)}</dd>
        <dt>{t('ui.envelope.income')}</dt>
        <dd class="mono">{life.job ? money(life.job.income) : t('ui.envelope.noJob')}</dd>
      </dl>
      {pending > 0 && <p class="hint">{t('ui.envelope.payQueued', { sum: money(pending) })}</p>}
      {inDebt && pending === 0 && (
        <button type="button" class="btn accent block" disabled={!canPay} onClick={pay}>
          {canPay ? t('ui.envelope.pay', { sum: money(sum) }) : t('ui.envelope.pay.noMoney')}
        </button>
      )}
      {minigames.length > 0 && (
        <div class="games">
          {minigames.map((def) => {
            const action = t(`ui.envelope.open.${def.id}`);
            return (
              <button key={def.id} type="button" class="btn block" disabled={busy} onClick={() => onOpen?.(def.id)}>
                {busy ? t('ui.envelope.busy', { action }) : action}
              </button>
            );
          })}
        </div>
      )}
      <ul class="later">
        {LATER_ACTIONS.map((a) => (
          <li key={a}>{t('ui.envelope.later', { action: t(`ui.envelope.action.${a}`) })}</li>
        ))}
      </ul>
    </details>
  );
}
